import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, X, Search, CheckCircle2 } from "lucide-react";
import { Modal } from "antd";
import axiosInstance from "../../utility/axiosInstance";
import Button2 from "../../components/elements/forms/Button2";
import { featuresService } from "../../services/api/settings";
import { Feature } from "../../store/types/settings";
import formatText from "../../utility/formatText";
import { offeringsService } from "../../services/api/settings";
import { OfferingPlan, OfferingAddon } from "../../store/types/settings";

type BusinessAcc = {
  id: string;
  name: string;
  address: string;
  registrationNo: string;
  contactEmail: string;
  contactPhone: string;
  representativeName: string;
  representativeEmail: string;
  representativeRole: string;
  challengeDescription: string;
  companySize: string;
  atsSystem: string;
  referralSource: string;
  createdAt: string;
  updatedAt: string;
};

const OrderForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const tier = searchParams.get("tier") || "";
  const provider = searchParams.get("provider");
  const frequencyParam = searchParams.get("frequency");
  const directPlanTier = searchParams.get("directPlan");
  const planIdParam = searchParams.get("planId");
  const includeSetupFee = searchParams.get("includeSetupFee");

  const [organization, setOrganization] = useState<BusinessAcc | null>(null);
  const [isAgreed, setIsAgreed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<Feature[]>([]);
  const [selectedDirectAddOns, setSelectedDirectAddOns] = useState<
    OfferingAddon[]
  >([]);
  const [selectedAIAddOns, setSelectedAIAddOns] = useState<OfferingAddon[]>([]);
  const [deploymentCosts, setDeploymentCosts] = useState<any[]>([]);
  const [integrationCosts, setIntegrationCosts] = useState<any[]>([]);
  const [integrationIds, setIntegrationIds] = useState<string[]>([]);
  const [isAddOnModalOpen, setIsAddOnModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [directFeatures, setDirectFeatures] = useState<any[]>([]);
  const [offeringPlans, setOfferingPlans] = useState<OfferingPlan[]>([]);
  const [offeringAddons, setOfferingAddons] = useState<OfferingAddon[]>([]);
  const [lifecyclePhases, setLifecyclePhases] = useState<any[]>([]);
  const [selectedLifecyclePhases, setSelectedLifecyclePhases] = useState<any[]>([]);

  // Fetch organization data
  useEffect(() => {
    const fetchOrganizationData = async () => {
      try {
        if (!id) {
          console.warn("Organization ID not found");
          setLoading(false);
          return;
        }

        const response = await axiosInstance.get(`/organizations/${id}/slight`);
        if (response.data?.status === "success" && response.data?.data) {
          setOrganization(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching organization data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizationData();
  }, [id]);

  // Fetch features and filter selected ones
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        if (!id) {
          console.warn("Organization ID not found");
          setLoading(false);
          return;
        }
        const response = await featuresService.getAll(id);
        if (response.status === "success" && response.data) {
          setFeatures(response.data);

          // Parse featureIds from URL
          const featureIdsParam = searchParams.get("featureIds");
          if (featureIdsParam) {
            const featureIds = featureIdsParam.split(",");
            const selected = response.data.filter((f: Feature) =>
              featureIds.includes(f.id),
            );
            setSelectedFeatures(selected);
          }

          // NOTE: Add-ons are synced in the offerings effect below
        }
      } catch (error) {
        console.error("Error fetching features:", error);
      }
    };
    fetchFeatures();
  }, [searchParams, id]);

  // Fetch Direct Features (Dynamic)
  useEffect(() => {
    const fetchDirectFeatures = async () => {
      try {
        const response = await featuresService.getDirectFeatures();
        if (response?.data && Array.isArray(response.data)) {
          setDirectFeatures(response.data);
        }
      } catch (error) {
        console.error("Error fetching direct features:", error);
      }
    };
    fetchDirectFeatures();
  }, []);

  // Fetch Offerings (New Dynamic API)
  useEffect(() => {
    const fetchOfferingsResponse = async () => {
      try {
        const response = await offeringsService.getAll();
        if (response.status === "success" && response.data) {
          // Flatten all plans and addons for easy lookup
          const allPlans = [
            ...response.data.CONSOLE.plans,
            ...response.data.RECRUITER.plans,
            ...response.data.DIRECT.plans,
          ];
          const allAddons = [
            ...response.data.CONSOLE.addons,
            ...response.data.RECRUITER.addons,
            ...response.data.DIRECT.addons,
          ];
          setOfferingPlans(allPlans);
          setOfferingAddons(allAddons);

          if (response.data.LIFECYCLE?.phases) {
            setLifecyclePhases(response.data.LIFECYCLE.phases);
          }

          // Sync selected add-ons from URL
          const directAddOnIdsParam =
            searchParams.get("directAddOnIds") ||
            searchParams.get("directAddOns");
          if (directAddOnIdsParam) {
            const directAddOnIds = directAddOnIdsParam.split(",");
            const selectedDA = response.data.DIRECT.addons.filter((d) =>
              directAddOnIds.includes(d.id),
            );
            setSelectedDirectAddOns(selectedDA);

            const selectedAI = response.data.RECRUITER.addons.filter((a) =>
              directAddOnIds.includes(a.id),
            );
            setSelectedAIAddOns(selectedAI);
          }
        }
      } catch (error) {
        console.error("Error fetching offerings:", error);
      }
    };
    fetchOfferingsResponse();
  }, [searchParams]);

  // Sync selected direct add-ons using fetched directFeatures
  useEffect(() => {
    const directAddOnIdsParam =
      searchParams.get("directAddOnIds") || searchParams.get("directAddOns");

    if (directAddOnIdsParam && directFeatures.length > 0) {
      const directAddOnIds = directAddOnIdsParam.split(",");
      // Use directFeatures for filtering to ensure we have the dynamic data
      const selectedDA = directFeatures.filter((d) =>
        directAddOnIds.includes(d.id),
      );
      // We merge with existing if they were set from DIRECT_ADDONS earlier,
      // but directFeatures should be authoritative.
      setSelectedDirectAddOns(selectedDA);
    }
  }, [searchParams, directFeatures]);

  // Sync selected lifecycle phases from URL
  useEffect(() => {
    const lifecyclePhaseIdsParam = searchParams.get("lifecyclePhaseIds");
    if (lifecyclePhaseIdsParam && lifecyclePhases.length > 0) {
      const idsArray = lifecyclePhaseIdsParam.split(",");
      const selected = lifecyclePhases.filter((p) =>
        idsArray.includes(p.id.toString()) || p.isMandatory
      );
      setSelectedLifecyclePhases(selected);
    } else if (lifecyclePhases.length > 0) {
      const mandatory = lifecyclePhases.filter((p) => p.isMandatory);
      setSelectedLifecyclePhases(mandatory);
    }
  }, [searchParams, lifecyclePhases]);

  // Parse deployment and integration costs
  useEffect(() => {
    const deploymentIdsParam = searchParams.get("deploymentIds");
    const integrationIdsParam = searchParams.get("integrationIds");

    // Deployment costs for Console plans
    const deploymentOptions = [
      {
        id: "bb65b52f-d251-4335-aa3f-b41792f1c1c4",
        name: "Virtual Deployment",
        price: 1500000,
      },
      {
        id: "6d88e65d-1483-4516-a43d-89367759635d",
        name: "On-Site Deployment",
        price: 1750000,
      },
      {
        id: "f7460a29-b423-45aa-9a89-92c18bd1599d",
        name: "Data Migration",
        price: 250000,
      },
      {
        id: "61f18817-9a1f-4a6c-9ea7-58432036f796",
        name: "Staff Training",
        price: 150000,
      },
    ];

    if (deploymentIdsParam) {
      const deploymentIds = deploymentIdsParam.split(",");
      const selected = deploymentOptions.filter((d) =>
        deploymentIds.includes(d.id),
      );
      setDeploymentCosts(selected);
    }

    // Integration costs for Console plans
    const integrationOptions = [
      {
        id: "b6b093b7-c55a-4734-bdbc-d07fabb3c851",
        name: "Corporate Email Integration",
        price: 700000,
      },
      {
        id: "a6b3c627-2bfb-425d-9f55-b34129171350",
        name: "SMS Gateway",
        price: 250000,
      },
      {
        id: "2978e19f-0275-438c-ad93-14dc41b272da",
        name: "Payment Gateway",
        price: 300000,
      },
      {
        id: "9367d93b-dbbc-40a0-a76b-6ea1f41b09d6",
        name: "Calendar Sync",
        price: 350000,
      },
      {
        id: "8e1de15d-e7f2-4dfe-a4ee-df7c10342087",
        name: "Biometric Attendance",
        price: 450000,
      },
      {
        id: "051f890e-1804-43f6-963f-d8d5752b0d0f",
        name: "Payroll & Payslip Generator",
        price: 25000,
      },
      {
        id: "f81e8f8b-ad5f-4601-840a-ee1f184433da",
        name: "Asset Management",
        price: 15000,
      },
      {
        id: "e28175ee-cd09-4c4b-9e91-f8f998a0b41f",
        name: "Expense & Requisition",
        price: 20000,
      },
      {
        id: "ea0583d5-310f-4e5b-b156-3c8453293f24",
        name: "Mobile App (Lite)",
        price: 40000,
      },
    ];

    if (integrationIdsParam) {
      const integrationIds = integrationIdsParam.split(",");
      const selected = integrationOptions.filter((i) =>
        integrationIds.includes(i.id),
      );
      setIntegrationCosts(selected);
      setIntegrationIds(integrationIds); // Ensure integrationIds state is updated
    }
  }, [searchParams]);

  const handleProceed = () => {
    if (!isAgreed) return;

    // Calculate total numeric amount
    const totalAmount =
      basePrice +
      setupFees +
      featuresTotal +
      directAddOnsTotal +
      aiAddOnsTotal +
      integrationsTotal +
      lifecycleTotal;

    // Navigate to payment-selection with all parameters
    const featureIdsParam =
      selectedFeatures.length > 0
        ? `&featureIds=${selectedFeatures.map((f) => f.id).join(",")}`
        : "";
    console.log("Feature IDs Param from Order Form:", featureIdsParam);
    console.log("Selected Features from Order Form:", selectedFeatures);

    const directAddOnsParam =
      selectedDirectAddOns.length > 0
        ? `&directAddOnIds=${selectedDirectAddOns.map((d) => d.id).join(",")}`
        : "";

    const lifecyclePhasesParam =
      searchParams.get("lifecyclePhaseIds")
        ? `&lifecyclePhaseIds=${searchParams.get("lifecyclePhaseIds")}`
        : "";

    const planIdParamStr = planIdParam ? `&planId=${planIdParam}` : "";

    navigate(
      `/sla-brief?Id=${id}${tier ? `&tier=${tier}` : ""}${
        provider ? `&provider=${provider}` : ""
      }${
        frequencyParam ? `&frequency=${frequencyParam}` : ""
      }${featureIdsParam}${directAddOnsParam}${lifecyclePhasesParam}${
        directPlanTier ? `&directPlan=${directPlanTier}` : ""
      }${includeSetupFee ? `&includeSetupFee=${includeSetupFee}` : ""}${
        searchParams.get("deploymentIds")
          ? `&deploymentIds=${searchParams.get("deploymentIds")}`
          : ""
      }${
        searchParams.get("integrationIds")
          ? `&integrationIds=${searchParams.get("integrationIds")}`
          : ""
      }${planIdParamStr}&amount=${totalAmount}`,
    );
  };

  // const handleBack = () => {
  //   const featureIdsParam =
  //     selectedFeatures.length > 0
  //       ? `&featureIds=${selectedFeatures.map((f) => f.id).join(",")}`
  //       : "";

  //   const directAddOnsParam =
  //     selectedDirectAddOns.length > 0
  //       ? `&directAddOns=${selectedDirectAddOns.map((d) => d.id).join(",")}`
  //       : "";

  //   const commonParams = `?Id=${id}${tier ? `&tier=${tier}` : ""}${
  //     frequencyParam ? `&frequency=${frequencyParam}` : ""
  //   }${featureIdsParam}${directAddOnsParam}${
  //     directPlanTier ? `&directPlan=${directPlanTier}` : ""
  //   }${includeSetupFee ? `&includeSetupFee=${includeSetupFee}` : ""}${
  //     searchParams.get("deploymentIds")
  //       ? `&deploymentIds=${searchParams.get("deploymentIds")}`
  //       : ""
  //   }${
  //     searchParams.get("integrationIds")
  //       ? `&integrationIds=${searchParams.get("integrationIds")}`
  //       : ""
  //   }`;

  //   if (directPlanTier || searchParams.get("deploymentIds")) {
  //     navigate(`/agreement${commonParams}`);
  //   } else {
  //     navigate(`/pay${commonParams}`);
  //   }
  // };

  // Get pricing based on tier and plan type
  const getPricingByTier = (tierName: string) => {
    const isDirect = tierName.startsWith("DIRECT_");
    const isConsole = searchParams.has("deploymentIds") && !isDirect;

    const plan = offeringPlans.find((p) => {
      if (isDirect && directPlanTier) {
        return p.tier === directPlanTier;
      }
      if (isConsole) {
        return p.title === tierName;
      }
      return p.tier === tierName;
    });

    if (plan) {
      return {
        name: plan.title,
        monthly: parseFloat(plan.monthlyPrice),
        yearly: parseFloat(plan.yearlyPrice),
        setupFee: parseFloat(plan.setupFee || "0"),
        integrationTotal: parseFloat(plan.integrationCost || "0"),
        description: plan.description,
        points: plan.points,
        tier: plan.tier,
      };
    }

    // Fallback if plans haven't loaded yet (minimal fallback to avoid crashes)
    return {
      name: tierName,
      monthly: 0,
      yearly: 0,
      setupFee: 0,
      integrationTotal: 0,
    };
  };

  const currentPricing = getPricingByTier(tier);
  const currentDate = new Date().toLocaleDateString();

  const frequency =
    frequencyParam?.toUpperCase() === "YEARLY" ? "YEARLY" : "MONTHLY";

  // Determine active plan components
  const hasConsolePlan = !!searchParams.get("deploymentIds");
  const hasDirectPlan = !!directPlanTier;
  const hasAIPlan = searchParams.has("tier") && !hasConsolePlan;
  // const hasAIPlan = searchParams.has("tier") && !directPlanTier;

  // Get specific pricing objects
  const aiPricing = hasAIPlan ? getPricingByTier(tier) : null;
  const directPricing = hasDirectPlan
    ? getPricingByTier(directPlanTier!)
    : null;
  const consolePricing = hasConsolePlan ? getPricingByTier(tier) : null;

  // Calculate base subscription prices
  const aiBasePrice = aiPricing
    ? frequency === "YEARLY"
      ? aiPricing.yearly
      : aiPricing.monthly
    : 0;
  const directBasePrice = directPricing
    ? frequency === "YEARLY"
      ? directPricing.yearly
      : directPricing.monthly
    : 0;
  const consoleBasePrice = consolePricing
    ? frequency === "YEARLY"
      ? consolePricing.yearly
      : consolePricing.monthly
    : 0;

  const basePrice = aiBasePrice + directBasePrice + consoleBasePrice;

  // Calculate setup/integration/deployment fees
  const calculateSetupFees = () => {
    let totalSetup = 0;

    if (hasDirectPlan && directPricing) {
      totalSetup +=
        includeSetupFee === "true" ? directPricing.setupFee || 0 : 0;
    }

    if (hasConsolePlan) {
      const deploymentTotal = deploymentCosts.reduce(
        (sum: number, d: any) => sum + d.price,
        0,
      );
      const integrationTotal = integrationCosts.reduce(
        (sum: number, i: any) => sum + i.price,
        0,
      );
      totalSetup += deploymentTotal + integrationTotal;
    }

    if (hasAIPlan && aiPricing) {
      totalSetup += aiPricing.integrationTotal || 0;
    }

    return totalSetup;
  };

  const setupFees = calculateSetupFees();

  // Calculate total of selected features (in kobo)
  const featuresTotal = selectedFeatures.reduce(
    (sum: number, feature: any) => sum + feature.price,
    0,
  );

  // Calculate total of selected Direct Add-ons
  const directAddOnsTotal = selectedDirectAddOns.reduce(
    (sum: number, addon: OfferingAddon) => {
      const numericPrice =
        typeof addon.price === "string" ? parseFloat(addon.price) : addon.price;
      return sum + (numericPrice || 0);
    },
    0,
  );

  // Calculate total of selected AI Add-ons
  const aiAddOnsTotal = selectedAIAddOns.reduce(
    (sum: number, addon: OfferingAddon) => {
      const numericPrice =
        typeof addon.price === "string" ? parseFloat(addon.price) : addon.price;
      return sum + (numericPrice || 0);
    },
    0,
  );

  // Calculate total of integration costs
  const integrationsTotal = integrationCosts.reduce(
    (sum: number, i: any) => sum + i.price,
    0,
  );

  const lifecycleTotal = selectedLifecyclePhases.reduce(
    (sum: number, phase: any) => {
      const priceVal =
        (phase.setupFee || 0) +
        (phase.monthlySubscription || 0) *
          (frequency === "YEARLY" ? 12 : 1);
      return sum + priceVal;
    },
    0,
  );

  const addonsTotal =
    featuresTotal +
    directAddOnsTotal +
    aiAddOnsTotal +
    integrationsTotal +
    lifecycleTotal;

  // Calculate total first invoice
  const calculateTotalFirstInvoice = () => {
    const total = basePrice + setupFees + addonsTotal;
    return `${total.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const capitalize = (str: string | undefined) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getModalAddOns = (): any[] => {
    const selectableAddons = offeringAddons.filter(
      (a: OfferingAddon) =>
        a.category === "RECRUITER" || a.category === "DIRECT",
    );
    return selectableAddons.filter((a: OfferingAddon) =>
      a.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const handleToggleAddOn = (
    item: any,
    type: "FEAT" | "DIRECT" | "AI" | "CONSOLE",
  ) => {
    if (type === "FEAT") {
      setSelectedFeatures((prev: any[]) =>
        prev.find((f) => f.id === item.id)
          ? prev.filter((f) => f.id !== item.id)
          : [...prev, item],
      );
    } else if (type === "DIRECT") {
      setSelectedDirectAddOns((prev: any[]) =>
        prev.find((a) => a.id === item.id)
          ? prev.filter((a) => a.id !== item.id)
          : [...prev, item],
      );
    } else if (type === "AI") {
      setSelectedAIAddOns((prev: any[]) =>
        prev.find((a) => a.id === item.id)
          ? prev.filter((a) => a.id !== item.id)
          : [...prev, item],
      );
    } else if (type === "CONSOLE") {
      setIntegrationCosts((prev: any[]) =>
        prev.find((i) => i.id === item.id)
          ? prev.filter((i) => i.id !== item.id)
          : [...prev, item],
      );
    }
  };

  const CONSOLE_OPTIONS = [
    {
      id: "b6b093b7-c55a-4734-bdbc-d07fabb3c851",
      name: "Corporate Email Integration",
      price: 700000,
    },
    {
      id: "a6b3c627-2bfb-425d-9f55-b34129171350",
      name: "SMS Gateway",
      price: 250000,
    },
    {
      id: "2978e19f-0275-438c-ad93-14dc41b272da",
      name: "Payment Gateway",
      price: 300000,
    },
    {
      id: "9367d93b-dbbc-40a0-a76b-6ea1f41b09d6",
      name: "Calendar Sync",
      price: 350000,
    },
    {
      id: "8e1de15d-e7f2-4dfe-a4ee-df7c10342087",
      name: "Biometric Attendance",
      price: 450000,
    },
    {
      id: "051f890e-1804-43f6-963f-d8d5752b0d0f",
      name: "Payroll & Payslip Generator",
      price: 25000,
    },
    {
      id: "f81e8f8b-ad5f-4601-840a-ee1f184433da",
      name: "Asset Management",
      price: 15000,
    },
    {
      id: "e28175ee-cd09-4c4b-9e91-f8f998a0b41f",
      name: "Expense & Requisition",
      price: 20000,
    },
    {
      id: "ea0583d5-310f-4e5b-b156-3c8453293f24",
      name: "Mobile App (Lite)",
      price: 40000,
    },
  ];

  if (loading) {
    return (
      <div className="font-abel min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2D6EEF] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-abel min-h-screen bg-white py-8 px-4">
      <div className="max-w-[1024px] mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-[32px] font-medium text-[#212121] mb-2">
            Order Form
          </h1>
          <p className="text-[18px] text-gray-600">
            {hasAIPlan && hasDirectPlan
              ? "Combined AI Recruiter & 25th Direct Service"
              : hasAIPlan
                ? "AI Recruiter"
                : hasDirectPlan
                  ? "25th Direct Service"
                  : "Console Platform"}{" "}
            Application Subscription
          </p>
          <p className="text-[14px] text-gray-500 mt-2">
            This Order Form is governed by the Enterprise Services Agreement
            Package. (ESAP) executed between 25th & Staffing ("Provider") and{" "}
            {organization?.name || "[Client Legal Entity Name]"} ("Customer").
          </p>
        </div>

        {/* Customer and Provider Info */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-[14px] font-medium text-gray-500 mb-2">
                Customer Name:
              </h3>
              <p className="text-[16px] text-[#212121]">
                {organization?.name || "[Client Name]"}
              </p>
            </div>
            <div></div>
            <div>
              <h3 className="text-[14px] font-medium text-gray-500 mb-2">
                Provider Name:
              </h3>
              <p className="text-[16px] text-[#212121]">25th & Staffing</p>
            </div>
            <div>
              <h3 className="text-[14px] font-medium text-gray-500 mb-2">
                Provider Address:
              </h3>
              <p className="text-[16px] text-[#212121]">
                Architect Place, 2 Idowu Taylor Street, Victoria Island, Lagos,
                Nigeria
              </p>
            </div>
            <div>
              <h3 className="text-[14px] font-medium text-gray-500 mb-2">
                Order Form Date:
              </h3>
              <p className="text-[16px] text-[#212121]">{currentDate}</p>
            </div>
            <div>
              <h3 className="text-[14px] font-medium text-gray-500 mb-2">
                SOW / Proposal:
              </h3>
              <p className="text-[16px] text-[#212121]">
                {hasAIPlan && hasDirectPlan
                  ? "AI Recruiter & 25th Direct Service"
                  : hasAIPlan
                    ? "AI Recruiter"
                    : hasDirectPlan
                      ? "25th Direct Service"
                      : "Console Platform"}{" "}
                {hasAIPlan && aiPricing?.name}
                {hasConsolePlan && capitalize(tier)} v1.5
              </p>
            </div>
          </div>
        </div>

        {/* 1. Subscription Services */}
        <div className="mb-8">
          <h2 className="text-[24px] font-medium text-[#212121] mb-4">
            1. Subscription Services
          </h2>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200">
                    Product / Service
                  </th>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200">
                    Tier
                  </th>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b">
                    Quantity
                  </th>
                </tr>
              </thead>
              <tbody>
                {hasAIPlan && aiPricing && (
                  <tr>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      AI Recruiter Platform
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      {aiPricing.name}
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      {aiPricing.description}
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 text-center">
                      1
                    </td>
                  </tr>
                )}
                {hasDirectPlan && directPricing && (
                  <tr>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      25th Direct Service
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      {directPricing.name}
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      Automated recruitment service with AI-powered features.
                      Includes standard support and platform access.
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 text-center">
                      1
                    </td>
                  </tr>
                )}
                {hasConsolePlan && consolePricing && (
                  <tr>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      Console Platform
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      {capitalize(tier)}
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      {consolePricing.description || "Console Platform access"}
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 text-center">
                      1
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 space-y-2">
            <p className="text-[14px] text-gray-700">
              <span className="font-medium">Initial Subscription Term:</span> 1
              {frequency === "YEARLY" ? " Year" : " Month"}
            </p>
            <p className="text-[14px] text-gray-700">
              <span className="font-medium">Billing Frequency:</span>{" "}
              {frequency === "YEARLY" ? "Yearly" : "Monthly"}
            </p>
            <p className="text-[14px] text-gray-700">
              <span className="font-medium">
                Monthly{frequency === "YEARLY" ? "/Yearly" : ""} Fee:
              </span>{" "}
              N
              {basePrice.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
            <p className="text-[14px] text-gray-700">
              <span className="font-medium">Subscription Start Date:</span>{" "}
              {currentDate}
            </p>
          </div>
        </div>

        {/* 2. Professional Services */}
        <div className="mb-8">
          <h2 className="text-[24px] font-medium text-[#212121] mb-4">
            2. Professional Services One-Time Fees
          </h2>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200">
                    Service
                  </th>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b">
                    Amount (NGN)
                  </th>
                </tr>
              </thead>
              <tbody>
                {hasDirectPlan &&
                  includeSetupFee === "true" &&
                  directPricing && (
                    <tr className="border-b border-gray-200">
                      <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                        Direct Setup Fee
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                        Platform configuration and onboarding for Direct Service
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-800">
                        {" "}
                        {directPricing.setupFee?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  )}

                {hasConsolePlan &&
                  deploymentCosts.map((deployment: any, idx: number) => (
                    <tr
                      key={deployment.id}
                      className="border-b border-gray-200"
                    >
                      <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                        {deployment.name}
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                        Console deployment service
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-800">
                        {" "}
                        {deployment.price.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}

                {hasAIPlan && aiPricing && (
                  <tr className="border-b border-gray-200">
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      Integration & Setup Fee
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                      Professional services including onboarding, training, and
                      configuration.
                    </td>
                    <td className="px-4 py-3 text-[14px] text-gray-800">
                      {" "}
                      {aiPricing.integrationTotal?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                )}

                <tr className="bg-gray-50">
                  <td
                    colSpan={2}
                    className="px-4 py-3 text-[14px] font-medium text-gray-900 text-right border-r border-gray-200"
                  >
                    Total One-Time Fee
                  </td>
                  <td className="px-4 py-3 text-[14px] font-bold text-gray-900">
                    {" "}
                    {setupFees.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 3. Optional Add-On Services */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[24px] font-medium text-[#212121]">
              3. Optional Add-On Services
            </h2>
            {!hasDirectPlan && (
              <button
                onClick={() => setIsAddOnModalOpen(true)}
                className="flex items-center text-[#2D6EEF] text-[14px] hover:text-[#1A4FBA] transition-colors"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Add-ons
              </button>
            )}
          </div>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            {selectedFeatures.length > 0 ||
            selectedDirectAddOns.length > 0 ||
            selectedAIAddOns.length > 0 ||
            selectedLifecyclePhases.length > 0 ||
            hasConsolePlan ? (
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200 w-12">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200">
                      Add-On Service
                    </th>
                    <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b">
                      Additional Fee
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedFeatures.map((feature: any, index: number) => (
                    <tr key={feature.id} className="border-b border-gray-200">
                      <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                        {formatText(feature.displayName)}
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-800">
                        <span className="font-[200]"></span>{" "}
                        {feature.price.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                  {selectedDirectAddOns.map(
                    (addon: OfferingAddon, index: number) => {
                      const priceVal =
                        typeof addon.price === "string"
                          ? parseFloat(addon.price)
                          : addon.price;
                      const displayPrice = `₦ ${priceVal.toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}`;

                      return (
                        <tr key={addon.id} className="border-b border-gray-200">
                          <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                            {selectedFeatures.length + index + 1}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                            {formatText(addon.name)}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-gray-800">
                            {displayPrice}
                          </td>
                        </tr>
                      );
                    },
                  )}
                  {selectedAIAddOns.map(
                    (addon: OfferingAddon, index: number) => {
                      const priceVal =
                        typeof addon.price === "string"
                          ? parseFloat(addon.price)
                          : addon.price;
                      const displayPrice = `₦ ${(priceVal || 0).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}`;

                      return (
                        <tr key={addon.id} className="border-b border-gray-200">
                          <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                            {selectedFeatures.length +
                              selectedDirectAddOns.length +
                              index +
                              1}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                            {formatText(addon.name)}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-gray-800">
                            {displayPrice}
                          </td>
                        </tr>
                      );
                    },
                  )}
                  {integrationCosts.map((integration: any, index: number) => (
                    <tr
                      key={integration.id}
                      className="border-b border-gray-200"
                    >
                      <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                        {selectedFeatures.length +
                          selectedDirectAddOns.length +
                          selectedAIAddOns.length +
                          index +
                          1}
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                        {formatText(integration.name)}
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-800">
                        {" "}
                        {integration.price.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                  {selectedLifecyclePhases.map((phase: any, index: number) => {
                    const priceVal =
                      (phase.setupFee || 0) +
                      (phase.monthlySubscription || 0) *
                        (frequency === "YEARLY" ? 12 : 1);
                    const displayPrice = `₦ ${priceVal.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}`;

                    return (
                      <tr key={phase.id} className="border-b border-gray-200">
                        <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                          {selectedFeatures.length +
                            selectedDirectAddOns.length +
                            selectedAIAddOns.length +
                            integrationCosts.length +
                            index +
                            1}
                        </td>
                        <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                          {formatText(phase.name)} (Employee Lifecycle Phase)
                        </td>
                        <td className="px-4 py-3 text-[14px] text-gray-800">
                          {displayPrice}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-50 border-t-2 border-gray-200">
                    <td
                      colSpan={2}
                      className="px-4 py-4 text-[15px] font-semibold text-gray-900 text-right border-r border-gray-200"
                    >
                      Sum of Add-On Services
                    </td>
                    <td className="px-4 py-4 text-[15px] font-bold text-[#2D6EEF]">
                      {" "}
                      NGN{" "}
                      {addonsTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <p className="text-[14px]">No add-on services selected</p>
              </div>
            )}
          </div>
        </div>

        {/* 4. Fees & Payment Schedule */}
        <div className="mb-8">
          <h2 className="text-[24px] font-medium text-[#212121] mb-4">
            4. Fees & Payment Schedule
          </h2>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200">
                    Line Item
                  </th>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b border-r border-gray-200">
                    Fee Type
                  </th>
                  <th className="px-4 py-3 text-left text-[14px] font-medium text-gray-700 border-b">
                    Amount (NGN)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                    {hasAIPlan && hasDirectPlan
                      ? "AI Recruiter & Direct Service"
                      : hasAIPlan
                        ? "AI Recruiter Platform"
                        : hasDirectPlan
                          ? "25th Direct Service"
                          : "Console Platform"}{" "}
                    ({frequency === "YEARLY" ? "Yearly" : "Monthly"} Fee)
                  </td>
                  <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                    Subscription
                  </td>
                  <td className="px-4 py-3 text-[14px] text-gray-800">
                    {" "}
                    {basePrice.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                    Total One-Time Fee
                  </td>
                  <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                    One-Time
                  </td>
                  <td className="px-4 py-3 text-[14px] text-gray-800">
                    {" "}
                    {setupFees.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                    Add-on Services
                  </td>
                  <td className="px-4 py-3 text-[14px] text-gray-800 border-r border-gray-200">
                    {frequency === "YEARLY" ? "Yearly" : "Monthly"}/One-Time
                  </td>
                  <td className="px-4 py-3 text-[14px] text-gray-800">
                    {" "}
                    {addonsTotal.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
                <tr className="bg-[#2D6EEF]/10">
                  <td
                    colSpan={2}
                    className="px-4 py-4 text-[16px] font-medium text-gray-900 text-right border-r border-gray-200"
                  >
                    Total Due: First Invoice
                  </td>
                  <td className="px-4 py-4 text-[16px] text-gray-900">
                    {calculateTotalFirstInvoice()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-[14px] text-gray-700 mb-2">
              <span className="font-medium">Payment Terms:</span>
            </p>
            <ul className="list-disc pl-6 space-y-1 text-[14px] text-gray-700">
              <li>
                The Total Due First Invoice of NGN{" "}
                {calculateTotalFirstInvoice()} covering the One-Time Fee and the
                first subscription will be invoiced upon execution of this Order
                Form.
              </li>
              <li>
                All subsequent monthly subscription fees will be invoiced 30
                days in advance of each billing cycle.
              </li>
            </ul>
          </div>
        </div>

        {/* Agreement Checkbox */}
        <div className="mb-8 border border-gray-300 rounded-lg p-6 bg-gray-50">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 text-[#2D6EEF] border-gray-300 rounded focus:ring-[#2D6EEF]"
            />
            <span className="text-[14px] text-gray-700">
              By checking this box, Client agrees to the terms of this Order
              Form and the governing Enterprise Services Agreement Package.
            </span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {isAgreed && (
            <Button2
              title="Proceed"
              className="bg-[#2D6EEF] text-white w-full sm:w-[250px] hover:bg-[#1E5DD8] border-none py-2 rounded-[8px]"
              onSubmitHandler={handleProceed}
            />
          )}
        </div>
      </div>
      <Modal
        title={null}
        open={isAddOnModalOpen}
        onCancel={() => setIsAddOnModalOpen(false)}
        footer={null}
        width={700}
        centered
        className="add-ons-modal"
      >
        <div className="p-2 font-abel">
          <div className="mb-6">
            <h2 className="text-2xl font-medium text-gray-900 mb-4">
              Add Extra Services
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search for add-ons..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            <div className="space-y-3">
              {getModalAddOns().map((item: any) => {
                const isSelected = hasAIPlan
                  ? selectedAIAddOns.some((a) => a.id === item.id)
                  : hasDirectPlan
                    ? selectedDirectAddOns.some((d) => d.id === item.id)
                    : integrationCosts.some((i) => i.id === item.id);

                let price = "";
                if (item.price) {
                  const priceValue = hasConsolePlan ? item.price : item.price;
                  price = priceValue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                } else if (item.pricing) {
                  const priceVal =
                    frequency === "YEARLY"
                      ? item.pricing.yearlyPrice
                      : item.pricing.monthlyPrice;
                  price =
                    typeof priceVal === "number"
                      ? priceVal.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : priceVal;
                }

                return (
                  <div
                    key={item.id}
                    onClick={() => {
                      const isAI = item.category === "RECRUITER";
                      const isDirect = item.category === "DIRECT";

                      handleToggleAddOn(
                        item,
                        isAI ? "AI" : isDirect ? "DIRECT" : "CONSOLE",
                      );
                    }}
                    className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer group ${
                      isSelected
                        ? "bg-blue-50 border-blue-200 shadow-sm"
                        : "bg-white border-gray-100 hover:border-blue-200"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          isSelected
                            ? "bg-blue-100 text-blue-600"
                            : "bg-gray-50 text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-600"
                        }`}
                      >
                        {isSelected ? (
                          <CheckCircle2 className="w-6 h-6" />
                        ) : (
                          <Plus className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {formatText(item.name) || item.displayName}
                        </p>
                        <p className="text-[13px] text-gray-800 font-medium">
                          {price}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        isSelected
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-600"
                      }`}
                    >
                      {isSelected ? "Selected" : "Add"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t flex justify-end">
            <button
              onClick={() => setIsAddOnModalOpen(false)}
              className="px-8 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition shadow-lg hover:shadow-blue-200"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
    </div>
  );
};

export default OrderForm;
